import React from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { TextField, Autocomplete, Grid, FormHelperText, TextFieldProps, InputAdornment } from '@mui/material';
import { countries } from '../../services/utils/lists';

export interface PhoneInputProps extends Omit<TextFieldProps, 'name' | 'onChange' | 'value'> {
    name: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ 
    name, 
    label, 
    required, 
    autoComplete,
    fullWidth,
    ...props
}) => {
    const { t } = useTranslation();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers] = useField(name);
    const [selectedCountry, setSelectedCountry] = React.useState(countries.find(c => c.code === 'MY') || countries[0]);
    const [phoneWithoutCode, setPhoneWithoutCode] = React.useState('');

    const handleCountryChange = (_: any, newValue: any) => {
        if (!newValue) return;
        setSelectedCountry(newValue);
        helpers.setValue(`+${newValue.phone}${phoneWithoutCode}`);
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        // Only allow numbers
        const phoneNumber = newValue.replace(/\D/g, '');
        setPhoneWithoutCode(phoneNumber);
        helpers.setValue(`+${selectedCountry.phone}${phoneNumber}`);
    };

    const showError = Boolean(meta.touched && meta.error);

    return (
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Autocomplete
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {option.name} (+{option.phone})
                        </li>
                    )}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('auth.countryCode')}
                            required={required}
                            error={showError}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    {...props}
                    label={label}
                    required={required}
                    autoComplete={autoComplete}
                    fullWidth={fullWidth}
                    onChange={handlePhoneChange}
                    error={showError}
                    value={phoneWithoutCode}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                +{selectedCountry.phone}
                            </InputAdornment>
                        ),
                        sx: { 
                            fontFamily: 'monospace',
                            '& .MuiInputAdornment-root': {
                                color: 'text.secondary',
                                userSelect: 'none'
                            }
                        }
                    }}
                />
            </Grid>
            {showError && (
                <Grid item xs={12}>
                    <FormHelperText error>{meta.error}</FormHelperText>
                </Grid>
            )}
        </Grid>
    );
};

export default PhoneInput;
